var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { action, makeObservable, observable } from 'mobx';
import { estimateRates } from '../methods/order';
const MIN_UPDATE_TIME = 5000;
const MAX_UPDATE_TIME = 60000;
export class ExchangeController {
    constructor(estimateRequestBody, amount, smartContractData, preservePaymentChannelState) {
        this.currentPaymentChannel = 'CARD_PAYMENT';
        this.isPaymentChannelExplicitlySelected = false;
        this.ttl = MIN_UPDATE_TIME;
        this.smartContractData = smartContractData;
        this.fee = null;
        this.fees = null;
        this.rate = null;
        this.timeout = undefined;
        this.exchangeInfo = null;
        this.error = false;
        this.exchangeDto = estimateRequestBody;
        this.amounts = amount;
        // Preserve payment channel state from previous instance if provided
        if (preservePaymentChannelState) {
            this.currentPaymentChannel = preservePaymentChannelState.currentPaymentChannel;
            this.isPaymentChannelExplicitlySelected = preservePaymentChannelState.isPaymentChannelExplicitlySelected;
        }
        makeObservable(this, {
            amounts: observable,
            fee: observable,
            fees: observable,
            rate: observable,
            error: observable,
            exchangeInfo: observable,
            currentPaymentChannel: observable,
            isPaymentChannelExplicitlySelected: observable,
            getRates: action,
            setPaymentChannel: action,
            setPaymentChannelExplicitly: action,
        });
    }
    getRates() {
        return __awaiter(this, void 0, void 0, function* () {
            clearTimeout(this.timeout);
            try {
                const res = yield estimateRates(this.exchangeDto);
                const data = updateAmount(res.data);
                this.ttl = Math.min(Math.max(data.rateTtl * 1000, MIN_UPDATE_TIME), MAX_UPDATE_TIME);
                this.error = false;
                // Use backend data directly - no hardcoded fee adjustments
                this.rate = data.rate;
                this.exchangeInfo = data;
                this.amounts = data.amounts;
                this.fee = data.fee;
                this.fees = data.fees || null;
                // Auto-switch to first available payment channel if current one is not available
                // Only do this if user hasn't explicitly selected a payment method
                if (this.fees && !this.isPaymentChannelExplicitlySelected) {
                    const availableChannels = Object.keys(this.fees);
                    // If current payment channel is not in the available fees, switch to the first available
                    if (availableChannels.length > 0 && !this.fees[this.currentPaymentChannel]) {
                        this.currentPaymentChannel = availableChannels[0];
                        this.exchangeDto.paymentChannel = availableChannels[0];
                    }
                }
            }
            catch (e) {
                this.error = true;
                throw e;
            }
            finally {
                this.timeout = setTimeout(() => {
                    this.getRates();
                }, this.ttl);
            }
        });
    }
    setPaymentChannel(channel) {
        this.currentPaymentChannel = channel;
        this.exchangeDto.paymentChannel = channel;
        // Trigger rate refresh to get updated fees for the new payment channel
        this.getRates();
    }
    setPaymentChannelExplicitly(channel) {
        this.currentPaymentChannel = channel;
        this.isPaymentChannelExplicitlySelected = true;
        this.exchangeDto.paymentChannel = channel;
        // Trigger rate refresh to get updated fees for the new payment channel
        this.getRates();
    }
    changeRequest(values) {
        return __awaiter(this, void 0, void 0, function* () {
            this.exchangeDto = getEstimateRequestDTOFromFields(Object.assign(Object.assign({}, values), { paymentChannel: this.currentPaymentChannel }));
            clearTimeout(this.timeout);
            yield this.getRates();
            return {
                rate: this.rate,
                amounts: this.amounts,
                fee: this.fee,
            };
        });
    }
    startWatching() {
        this.getRates();
    }
    stopWatching() {
        clearTimeout(this.timeout);
        this.timeout = undefined;
    }
}
export function getDefaultAmounts(defaultDto, currencies) {
    if (!defaultDto) {
        throw Error();
    }
    return {
        from: {
            currency: currencies.get(defaultDto.amounts.from.currency),
            amount: 'amount' in defaultDto.amounts.from
                ? `${defaultDto.amounts.from.amount}`
                : '0',
        },
        to: {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            currency: currencies.get(defaultDto.amounts.to.currency),
            amount: 'amount' in defaultDto.amounts.to
                ? `${defaultDto.amounts.to.amount}`
                : '0',
        },
        isError: false,
    };
}
function getEstimateRequestDTOFromFields({ fromAmount, toAmount, fromCurrency, toCurrency, direction, paymentChannel, }) {
    const amounts = direction === 'from'
        ? {
            from: { amount: +fromAmount, currency: fromCurrency.a3 },
            to: { currency: toCurrency.a3 },
        }
        : {
            from: { currency: fromCurrency.a3 },
            to: { currency: toCurrency.a3, amount: +toAmount },
        };
    return {
        amounts: amounts,
        paymentChannel
    };
}
function updateAmount(data) {
    const toAmount = Number(data.amounts.to.amount);
    const finalToAmount = toAmount.toString();
    return Object.assign(Object.assign({}, data), { amounts: Object.assign(Object.assign({}, data.amounts), { from: Object.assign(Object.assign({}, data.amounts.from), { amount: Number(data.amounts.from.amount).toString() }), to: Object.assign(Object.assign({}, data.amounts.to), { amount: finalToAmount }) }) });
}
export function createExchangeController(currencies, order, defaultAmount = 100, smartContractData, isEuro, existingController) {
    var _a;
    const defaultEstimateRequestBody = getDefaultEstimateRequestBody(currencies, order, defaultAmount, smartContractData, 
    // saving CCD case that are not in EU but want EUR on top
    true);
    const defaultAmounts = getDefaultAmounts(defaultEstimateRequestBody, currencies.currenciesMap);
    const defaultSmartContractData = smartContractData || ((_a = order === null || order === void 0 ? void 0 : order.metadata) === null || _a === void 0 ? void 0 : _a.smartContractData);
    // Preserve payment channel state from existing controller
    const preservePaymentChannelState = existingController ? {
        currentPaymentChannel: existingController.currentPaymentChannel,
        isPaymentChannelExplicitlySelected: existingController.isPaymentChannelExplicitlySelected
    } : undefined;
    return new ExchangeController(defaultEstimateRequestBody, defaultAmounts, defaultSmartContractData, preservePaymentChannelState);
}
export function getDefaultEstimateRequestBody(currencies, order, defaultAmount, smartContractData, isEuro) {
    var _a, _b;
    const currencyCodeByGeo = isEuro ? 'EUR' : 'USD';
    const defaultFromCurrency = ((_a = currencies.currenciesMap.get(currencyCodeByGeo)) === null || _a === void 0 ? void 0 : _a.a3) ||
        currencies.from[0].a3;
    const defaultToCurrency = Array.isArray(currencies.to[0])
        ? currencies.to[0][0].a3
        : currencies.to[0].a3;
    if ((_b = order === null || order === void 0 ? void 0 : order.metadata) === null || _b === void 0 ? void 0 : _b.smartContractData) {
        return {
            amounts: {
                from: {
                    currency: order.amounts.from.currency.a3,
                },
                to: {
                    amount: +order.amounts.to.amount,
                },
            },
            metadata: order.metadata,
        };
    }
    if (order === null || order === void 0 ? void 0 : order.amounts) {
        return {
            amounts: {
                from: {
                    amount: +order.amounts.from.amount,
                    currency: order.amounts.from.currency.a3,
                },
                to: {
                    currency: order.amounts.to.currency.a3,
                },
            },
        };
    }
    if (smartContractData) {
        const amounts = {
            from: {
                currency: defaultFromCurrency,
            },
            to: {
                amount: 1,
            },
        };
        return {
            amounts,
            metadata: {
                smartContractData: Object.assign(Object.assign({}, smartContractData), { imageUrl: smartContractData.imgUrl, imageName: smartContractData.name }),
            },
        };
    }
    return {
        amounts: {
            from: {
                amount: defaultAmount,
                currency: defaultFromCurrency,
            },
            to: {
                currency: defaultToCurrency,
            },
        },
    };
}
